/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * User Access Hook
 *
 * Centralized hook for checking user permissions and access levels.
 * Replaces scattered boolean checks across components.
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { BoxAccess, CustomClaims, AccessTier, Permission } from "@lemonadetraining/contracts";
import {
  ACCESS_TIER,
  PERMISSION,
  ROLE_PERMISSIONS,
  LEGACY_ACCESS_LEVEL_TO_TIER,
  resolveBoxAccess,
  resolveBoxLicense,
} from "@lemonadetraining/contracts";
import { useBoxOwnerUID } from "./useBoxOwnerUID";

/* ============================================================
 * Types
 * ============================================================ */

// Redux state type for user slice
interface RootState {
  user: {
    claims: CustomClaims | null;
    user: { uid?: string | null } | null;
    isLoggedIn: boolean;
    userInfoData: Record<string, any>;
  };
}

// Return type for the hook
export interface UserAccess {
  // === Raw Claims ===
  claims: CustomClaims | null;

  // === Access Tier ===
  accessTier: AccessTier;

  // === Scope ===
  boxID: string | null;
  coachType: "independent" | "box" | null;

  // === Convenience Booleans ===
  isLoggedIn: boolean;
  isAthlete: boolean;
  isBoxAthlete: boolean;
  isBoxCoach: boolean; // Coach employed by a box; admin implies coach, so true for every box admin
  isIndependentCoach: boolean; // Self-employed coach
  isBoxAdmin: boolean;
  isBoxStaff: boolean; // isBoxAdmin || isBoxCoach
  isOwner: boolean; // uid === Box.ownerUID (document truth, never a claim)
  // The highest hat this person wears at their gym, ranked by the types
  // package so a new BoxRole cannot silently reorder it. Null off a gym.
  displayRole: BoxAccess["displayRole"];
  isLemonadeAdmin: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean; // Any admin level (lemonade or super)

  // === Combined Checks (replaces old hasXxxRights) ===
  hasCoachRights: boolean; // Can access coach features
  hasBoxRights: boolean; // Can access box features
  hasAdminRights: boolean; // Can access admin features
  // Holds the platform gym licence and has no gym yet: the Business Portal
  // opens on the Create Gym form. Never true beside a gym. Discovery only:
  // createBox does not require the licence.
  canSetUpGym: boolean;

  // === Permission Functions ===
  can: (permission: Permission) => boolean;
  canAll: (permissions: Permission[]) => boolean;
  canAny: (permissions: Permission[]) => boolean;

  // === Tier Functions ===
  hasMinimumTier: (tier: AccessTier) => boolean;

  // === Loading State ===
  isLoading: boolean;
}

/* ============================================================
 * Helper: Get effective access tier from claims
 * Supports both new accessTier and legacy accessLevel
 * ============================================================ */
const getEffectiveAccessTier = (claims: CustomClaims | null): AccessTier => {
  if (!claims) {
    return ACCESS_TIER.GUEST;
  }

  // Prefer new accessTier if available
  if (claims.accessTier !== undefined && claims.accessTier !== null) {
    return claims.accessTier as AccessTier;
  }

  // Fall back to legacy accessLevel mapping
  if (claims.accessLevel !== undefined && claims.accessLevel !== null) {
    return LEGACY_ACCESS_LEVEL_TO_TIER[claims.accessLevel] ?? ACCESS_TIER.ATHLETE;
  }

  // Default to athlete
  return ACCESS_TIER.ATHLETE;
};

/* ============================================================
 * Helper: Get permissions for claims based on boolean flags
 * This maps the legacy boolean flags to the new permission system
 * ============================================================ */
const getPermissionsFromClaims = (
  claims: CustomClaims | null,
  box: BoxAccess
): readonly Permission[] => {
  if (!claims) {
    return [];
  }

  const permissions: Permission[] = [];

  // Admin has all permissions
  if (claims.admin) {
    return [...Object.values(PERMISSION)];
  }

  // Lemonade Admin
  if (claims.lemonadeAdmin) {
    return [...ROLE_PERMISSIONS["lemonade_admin"]];
  }

  // Box Admin — box_admin ⊇ box_coach is asserted in the types package
  if (box.isBoxAdmin) {
    return [...ROLE_PERMISSIONS["box_admin"]];
  }

  // Independent Coach
  if (claims.coach && claims.coachType === "independent") {
    return [...ROLE_PERMISSIONS["coach"]];
  }

  // Box Coach
  if (box.isBoxCoach) {
    return [...ROLE_PERMISSIONS["box_coach"]];
  }

  // Box Athlete (has boxID but not a coach/admin)
  if (claims.boxID && claims.athlete) {
    return [...ROLE_PERMISSIONS["box_athlete"]];
  }

  // Basic Athlete
  if (claims.athlete) {
    return [...ROLE_PERMISSIONS["athlete"]];
  }

  return permissions;
};

/* ============================================================
 * Hook: useUserAccess
 * ============================================================ */
export const useUserAccess = (): UserAccess => {
  const claims = useSelector((state: RootState) => state.user.claims);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const userInfoData = useSelector((state: RootState) => state.user.userInfoData);
  const uid = useSelector((state: RootState) => state.user.user?.uid ?? null);
  const boxID = claims?.boxID ?? userInfoData?.boxID ?? null;
  const { ownerUID } = useBoxOwnerUID(boxID);

  return useMemo(() => {
    // Get effective access tier
    const accessTier = getEffectiveAccessTier(claims);

    const box = resolveBoxAccess(claims, { uid, ownerUID });

    // Get permissions based on claims
    const userPermissions = getPermissionsFromClaims(claims, box);

    // === Convenience Booleans ===
    const isAthlete = claims?.athlete === true;
    const isBoxCoach = box.isBoxCoach;
    const isIndependentCoach = claims?.coach === true && claims?.coachType === "independent";
    const isBoxAdmin = box.isBoxAdmin;
    const isBoxStaff = box.isStaff;
    const isOwner = box.isOwner;
    const isLemonadeAdmin = claims?.lemonadeAdmin === true;
    const isSuperAdmin = claims?.admin === true;
    const isAdmin = isLemonadeAdmin || isSuperAdmin;

    // Internal: any type of coach (used for combined checks, not exposed)
    const _isAnyCoach = claims?.coach === true || box.isBoxCoach;

    // Box athlete: has active box membership but not a coach/admin
    const isBoxAthlete =
      isAthlete &&
      !_isAnyCoach &&
      !isBoxAdmin &&
      !isAdmin &&
      (claims?.boxID != null || userInfoData?.boxID != null);

    // === Combined Rights (backward compatible) ===
    const hasAdminRights = isAdmin;
    const hasCoachRights = _isAnyCoach || isAdmin;
    const hasBoxRights = isBoxAdmin || isAdmin;
    const canSetUpGym = resolveBoxLicense(claims) && boxID === null;

    // === Permission Functions ===
    const can = (permission: Permission): boolean => {
      // Super admin can do everything
      if (isSuperAdmin) return true;
      return userPermissions.includes(permission);
    };

    const canAll = (permissions: Permission[]): boolean => {
      return permissions.every((p) => can(p));
    };

    const canAny = (permissions: Permission[]): boolean => {
      return permissions.some((p) => can(p));
    };

    // === Tier Functions ===
    const hasMinimumTier = (tier: AccessTier): boolean => {
      return accessTier >= tier;
    };

    return {
      // Raw claims
      claims,

      // Access tier
      accessTier,

      // Scope
      boxID,
      coachType: claims?.coachType ?? null,

      // Convenience booleans
      isLoggedIn,
      isAthlete,
      isBoxAthlete,
      isBoxCoach,
      isIndependentCoach,
      isBoxAdmin,
      isBoxStaff,
      isOwner,
      displayRole: box.displayRole,
      isLemonadeAdmin,
      isSuperAdmin,
      isAdmin,

      // Combined checks (backward compatible)
      hasCoachRights,
      hasBoxRights,
      hasAdminRights,
      canSetUpGym,

      // Permission functions
      can,
      canAll,
      canAny,

      // Tier functions
      hasMinimumTier,

      // Loading state (claims not yet loaded)
      isLoading: isLoggedIn && claims === null,
    };
  }, [claims, isLoggedIn, boxID, uid, ownerUID]);
};

/* ============================================================
 * Re-export constants for convenience
 * ============================================================ */
export { ACCESS_TIER, PERMISSION } from "@lemonadetraining/contracts";
export type { AccessTier, Permission } from "@lemonadetraining/contracts";
